import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Icon, IconButton, Typography } from "@/components/atoms";
import { AddEditLocationModal } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/components/AddEditLocationModal";
import { suggestionIcon } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/components/LocationAddressAutocomplete";
import { MIN_CHARS, useAddressSearch } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/components/useAddressSearch";
import {
  cleanQuery,
  filterPoisByCountry,
  poiFieldLabel,
  geocoderResultToPoi,
} from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { useBookingData } from "@/components/pages/Client/booking/hooks/useBookingData";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { extractTerminal, isAirportPickup } from "@/helpers/bookingHelpers";
import { useCurrentLocation } from "@/hooks/useCurrentLocation";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { useListClientPoisQuery, useListClientRecentPoisQuery } from "@/redux/apis/clientPoi/clientPoiApi";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";
import { useListProviderPoisQuery } from "@/redux/apis/providerPoi/providerPoiApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { isUnvalidatedLocation, selectIsJobVerified } from "@/redux/slices/clientBooking/selectors";
import {
  addStop,
  firstEmptyField,
  isSameField,
  removeStop,
  setActiveField,
  setDestination,
  setFieldLocation,
  setIsPinning,
  setPickup,
  setStop,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectActiveBookingField,
  selectDestination,
  selectIsPinning,
  selectPickup,
  selectPickupFlightNumber,
  selectStops,
} from "@/redux/slices/clientBooking/selectors";
import { ActiveBookingField } from "@/redux/slices/clientBooking/types";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { clsx } from "@/utils";
import { BookingLocationInput } from "./BookingLocationInput";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { FlightDetailsModal } from "./FlightDetailsModal";
import { PinLocationModal } from "./PinLocationModal";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import { useAddressSearchCountries } from "./hooks/useAddressSearchCountries";
import { useFlightCapture } from "./hooks/useFlightCapture";

interface LocationPanelProps {
  className?: string;
  /** Advance to the Trip Details step ("Next Step" button). */
  onNext?: () => void;
}

const ADDRESS_INFO = "Enter street address only (avoid apartment or unit numbers)";
/*
 * Word for word what the bookings table shows in red, so an operator who clicked through from that
 * warning meets the same words rather than wondering whether they found the right booking.
 */
const UNVALIDATED_ADDRESS = "Requires Validation";

const poiTitle = (poi: PointsOfInterest) => poi.rm_label?.trim() || poi.address?.split(",")[0]?.trim() || poi.address;
const poiKey = (poi: PointsOfInterest) => poi.uuid || poi.vendor_uuid || poi.address;
const hasTag = (poi: PointsOfInterest, tag: string) => poi.rm_tags?.includes(tag);

// Whether two POIs are the same place. A saved favourite carries a uuid + (usually) the Google place id
// it was saved from; a freshly picked booking POI has only vendor_uuid/address. Fall through the
// strongest identifier to the weakest — but never coordinates alone (distinct places can share a
// snapped point, which would fill the wrong star).
const isSamePoi = (a: PointsOfInterest, b: PointsOfInterest) => {
  if (a.uuid && b.uuid) return a.uuid === b.uuid;
  if (a.vendor_uuid && b.vendor_uuid) return a.vendor_uuid === b.vendor_uuid;
  return !!a.address && a.address === b.address;
};

/** Dark circular clear (✕) shown while the user is typing an address. */
const ClearButton = ({ onClear }: { onClear: () => void }) => (
  <button
    type="button"
    aria-label="Clear"
    // preventDefault on mousedown keeps the input focused (so the results surface doesn't close and
    // swallow the click); the actual clear runs on click.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClear}
    className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-dark-gray text-white"
  >
    <Icon name="Add" size="sm" className="rotate-45" />
  </button>
);

// Favourite star shown once a location is committed. `saved` renders the same-coloured star as a
// FILLED (Bold) icon to signal it's already in the user's favourites; unsaved shows the outline and
// opens the "Save to Favourites" modal. An already-saved star is inert (no modal). preventDefault on
// mousedown keeps the input focused so the results surface doesn't swallow the click.
const FavouriteButton = ({ saved, onClick }: { saved: boolean; onClick: () => void }) => (
  <button
    type="button"
    aria-label={saved ? "Saved to favourites" : "Save to favourites"}
    aria-pressed={saved}
    onMouseDown={(e) => e.preventDefault()}
    onClick={saved ? undefined : onClick}
    className={saved ? "cursor-default" : undefined}
  >
    <Icon name="Star1" variant={saved ? "Bold" : "Linear"} size="md" className="text-neutral-dark-gray" />
  </button>
);

const SuggestionRow = ({ poi, onPick }: { poi: PointsOfInterest; onPick: (poi: PointsOfInterest) => void }) => {
  const icon = suggestionIcon(poi.rm_tags, false);
  return (
    <button
      type="button"
      // preventDefault keeps the active input focused, so picking never counts as leaving the form.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onPick(poi)}
      className="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-neutral-surface-gray"
    >
      <Icon name={icon.name} size="sm" className={clsx("mt-0.5 shrink-0", icon.className)} />
      <span className="min-w-0">
        <Typography variant="action" className="block truncate">
          {poiTitle(poi)}
        </Typography>
        <Typography variant="small-regular" className="block truncate text-neutral-dark-gray">
          {poi.address}
        </Typography>
      </span>
    </button>
  );
};

const GroupLabel = ({ children }: { children: string }) => (
  <Typography variant="small-regular" className="mb-1 mt-3 block text-neutral-dark-gray">
    {children}
  </Typography>
);

const ToggleLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className="mx-auto my-1 block">
    <Typography variant="action" className="text-primary">
      {label}
    </Typography>
  </button>
);

/**
 * The floating "Address" panel. Plain inputs (pickup/stops/destination) write into
 * clientBookingSlice; one shared results surface below them serves whichever field is active
 * (`activeField`) — the grouped Current location / Suggested / Recent / Home·Work / Favourites
 * lists by default, live search results once the field's text is edited to 3+ chars, nothing (plus
 * the Next Step footer) when no field is active. "Pin Location on Map" opens PinLocationModal for
 * the active field.
 */
export const LocationPanel = ({ className, onNext }: LocationPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  const jobVerified = useAppSelector(selectIsJobVerified);
  const pickup = useAppSelector(selectPickup);
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const destination = useAppSelector(selectDestination);
  const stops = useAppSelector(selectStops);
  const activeField = useAppSelector(selectActiveBookingField);
  const isPinning = useAppSelector(selectIsPinning);
  const operatorId = useAppSelector(operatorIdSelector);
  const { accountId } = useClientScope();
  const scope = accountId ? { accountId } : undefined;
  // Guest flow has no account — the recent/favourite POI reads are account-scoped (/v3/client/{id}/*)
  // and would 401. Skip them; the guest still gets provider POIs + live Google search.
  const { isGuest } = useGuestBooking();
  const userLocation = useCurrentLocation();
  // The provider's bookable list for an operator booking on behalf, its base country for everyone else.
  // Applied to the typed search, the saved places offered below, and the pin-on-map result.
  const searchCountries = useAddressSearchCountries();
  // Below md the results surface becomes a full-screen takeover (native-app pattern) and the
  // card keeps its Next Step footer; at md+ the surface is inline and focus-driven.
  const isMdUp = useIsMdUp();

  const { data: recentAll = [] } = useListClientRecentPoisQuery(scope, { skip: isGuest });
  const { data: favouritesAll = [] } = useListClientPoisQuery(scope, { skip: isGuest });
  const { data: providerPoisAll = [] } = useListProviderPoisQuery(undefined, { skip: !operatorId });
  // Only places inside the countries this booking may use are offered (a POI with no country code is kept).
  const recent = useMemo(() => filterPoisByCountry(recentAll, searchCountries), [recentAll, searchCountries]);
  const favourites = useMemo(() => filterPoisByCountry(favouritesAll, searchCountries), [favouritesAll, searchCountries]);
  const providerPois = useMemo(() => filterPoisByCountry(providerPoisAll, searchCountries), [providerPoisAll, searchCountries]);
  // Stop cap comes from the job/data `max_waypoints` (mobile parity); 0 until loaded → no Add Stop.
  // Both flows read it off the job/data the wizard already fetches (guest + authed hydrate the same
  // entity). The old authed source, /v1/provider's settings.max_waypoints, resolves to the same
  // number (same ff-booking-config-max-stops flag) but is keyed by activeOperator.providerCode — so
  // it reads 0, and hides "Add Stop", wherever that isn't loaded. See authApi's getProviderConfig.
  const { data: bookingData } = useBookingData();
  const maxStops = bookingData?.max_waypoints ?? 0;

  // A committed location already in the user's favourites shows a filled star (guests never see it).
  const isFavourite = (poi: PointsOfInterest) => favourites.some((f) => isSamePoi(f, poi));

  // The committed location whose favourite-save modal is open (null = closed).
  const [savePoi, setSavePoi] = useState<PointsOfInterest | null>(null);
  const [currentLocationPoi, setCurrentLocationPoi] = useState<PointsOfInterest | null>(null);
  const [showAllSuggested, setShowAllSuggested] = useState(false);
  const [showAllRecent, setShowAllRecent] = useState(false);

  // Opens once when an airport pickup is selected, via any path (autocomplete, suggestion tap, map
  // pin). Tracked by pickup identity so it doesn't re-open after being dismissed for the same pickup;
  // selecting a different airport re-prompts.
  const [flightModalOpen, setFlightModalOpen] = useState(false);
  const { confirmFlightNumber, notFlying } = useFlightCapture();
  // Seeded with the mount-time pickup ONLY when a flight number is already captured — remounting the
  // step (Previous from Trip Details) must not re-prompt for a handled airport pickup. A pre-filled
  // airport pickup with no flight (e.g. the guest widget → booking handoff) is NOT seeded, so the
  // effect below still prompts for the flight the first time.
  const promptedPickupKey = useRef<string | null>(pickup && isAirportPickup(pickup) && pickupFlightNumber ? poiKey(pickup) : null);
  const isAirportPickupSelected = useMemo(() => isAirportPickup(pickup), [pickup]);
  const pickupTerminal = useMemo(() => extractTerminal(pickup), [pickup]);

  const [pickupText, setPickupText] = useState(pickup ? poiFieldLabel(pickup) : "");
  const [destinationText, setDestinationText] = useState(destination ? poiFieldLabel(destination) : "");
  const [stopTexts, setStopTexts] = useState<string[]>(stops.map((s) => (s ? poiFieldLabel(s) : "")));

  // DOM focus follows activeField: rows/pin/Add Stop keep the input focused via mousedown
  // preventDefault, so activeField (not raw DOM focus) is the single source of truth for which
  // field the results surface serves.
  const fieldKey = (field: ActiveBookingField) => (field.kind === "stop" ? `stop-${field.index}` : field.kind);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const registerInput = (key: string) => (el: HTMLInputElement | null) => {
    if (el) inputRefs.current[key] = el;
    else delete inputRefs.current[key];
  };
  // While any of our dialogs is open (or leaving) its focus trap owns DOM focus — don't fight it.
  // On close the dialog restores focus to the field's input, whose onFocus re-activates it; the
  // surface and focus stay consistent without us racing the restore.
  const modalOwnsFocus = isPinning || flightModalOpen || !!savePoi;
  const takeoverInputRef = useRef<HTMLInputElement | null>(null);
  // When the Pin-on-Map modal closes, headlessui restores DOM focus to the field input that was focused
  // when it opened — i.e. the field we just pinned. That refocus fires the input's onFocus and would
  // re-activate the (now already-filled) field, diverging from a normal address pick (which advances to
  // the next empty field / releases focus). This flag lets that one restore-focus be ignored so the
  // pin flow ends in the same state as typing-and-selecting. Auto-cleared shortly after (past the
  // restore window) so a genuine click to re-edit a filled field still works.
  const ignorePinnedRefocusRef = useRef(false);
  useEffect(() => {
    if (!isPinning) return;
    return () => {
      ignorePinnedRefocusRef.current = true;
      window.setTimeout(() => (ignorePinnedRefocusRef.current = false), 500);
    };
  }, [isPinning]);
  useEffect(() => {
    if (modalOwnsFocus) return;
    if (activeField) {
      // md+ focuses the card input for the field; the takeover has its own single input.
      (isMdUp ? inputRefs.current[fieldKey(activeField)] : takeoverInputRef.current)?.focus();
      return;
    }
    // Nothing active (last field just filled, or the user left the form) — release DOM focus too,
    // so a blurred-looking form can't keep swallowing keystrokes.
    const focused = document.activeElement;
    if (focused instanceof HTMLInputElement && Object.values(inputRefs.current).includes(focused)) focused.blur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField, modalOwnsFocus, isMdUp]);

  // Walk the user to the first gap on entry; with everything already set (e.g. returning from
  // Trip Details) stay blurred so the resolved blank-body state shows. On mobile the step lands
  // on the compact card (per Figma) — the takeover only opens when the user taps an input.
  useEffect(() => {
    dispatch(setActiveField(isMdUp ? firstEmptyField(pickup, stops, destination) : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resizing across the breakpoint mid-browse: crossing INTO mobile lands on the card (the
  // takeover must not pop open uninvited); crossing into desktop keeps whatever was active.
  const prevMdUp = useRef(isMdUp);
  useEffect(() => {
    if (prevMdUp.current === isMdUp) return;
    prevMdUp.current = isMdUp;
    if (!isMdUp) dispatch(setActiveField(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMdUp]);

  // Focus moving anywhere inside the panel (a sibling input, a results row, Remove/Add Stop, the
  // flight button) isn't leaving; a dialog's focus trap must not clear the field it's about to
  // write into; and window deactivation (alt-tab) blurs with no relatedTarget but isn't leaving
  // either. Anything else means "done browsing" — close the results surface. One handler serves
  // input blurs and blurs bubbling out of the surface (Tab past the last row) alike.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isOurs = (el: EventTarget | null) => el instanceof Node && !!rootRef.current?.contains(el);
  const handleBlurAway = (event: React.FocusEvent<HTMLElement>) => {
    // Mobile: the takeover closes only via the back arrow or completing the walk-through —
    // dismissing the keyboard blurs the input but must not close it.
    if (!isMdUp || modalOwnsFocus || !document.hasFocus() || isOurs(event.relatedTarget)) return;
    dispatch(setActiveField(null));
  };

  // What the active field has typed SINCE focusing. Reset on every focus move so re-focusing a
  // filled input shows the default list (not a self-search for the value already picked); only an
  // actual edit switches the body to live search results.
  const [searchText, setSearchText] = useState("");
  useEffect(() => setSearchText(""), [activeField]);
  const { localMatches, remoteMatches, remotePending, resolvePlace } = useAddressSearch({
    query: searchText,
    enabled: !!activeField,
    includeFavourites: true,
    userLocation,
    countries: searchCountries,
  });
  const isSearching = !!activeField && cleanQuery(searchText).length >= MIN_CHARS;

  useEffect(() => {
    if (pickup) setPickupText(poiFieldLabel(pickup));
  }, [pickup]);

  useEffect(() => {
    if (pickup && isAirportPickupSelected) {
      const key = poiKey(pickup);
      if (promptedPickupKey.current !== key) {
        promptedPickupKey.current = key;
        /*
         * Only ASK when there's nothing captured yet.
         *
         * The ref above is seeded with the key when a flight number already exists, which was meant to
         * cover this — but it only works when the draft is populated at mount. Editing an existing
         * booking hydrates it from an API call, so the first render sees no pickup, the ref starts
         * null, and the modal opens the moment the pickup lands. Anyone reopening an airport booking
         * was asked to re-confirm a flight number that was already on it.
         */
        if (!pickupFlightNumber) setFlightModalOpen(true);
      }
    } else {
      // Pickup cleared or no longer an airport — close the modal so it can't linger over a non-airport pickup.
      promptedPickupKey.current = null;
      setFlightModalOpen(false);
    }
  }, [pickup, isAirportPickupSelected, pickupFlightNumber]);
  useEffect(() => {
    if (destination) setDestinationText(poiFieldLabel(destination));
  }, [destination]);
  useEffect(() => {
    setStopTexts((prev) => stops.map((s, i) => (s ? poiFieldLabel(s) : prev[i] ?? "")));
  }, [stops]);

  // Reverse-geocode the browser location once for the "Current location" row.
  useEffect(() => {
    if (!userLocation || currentLocationPoi || !window.google?.maps?.Geocoder) return;
    new window.google.maps.Geocoder().geocode({ location: userLocation }, (results, status) => {
      if (status === "OK" && results?.[0]) setCurrentLocationPoi(geocoderResultToPoi(results[0], userLocation));
    });
  }, [userLocation, currentLocationPoi]);

  const homeWork = useMemo(() => favourites.filter((f) => hasTag(f, RM_TAGS.Home) || hasTag(f, RM_TAGS.Work)), [favourites]);
  const otherFavourites = useMemo(() => favourites.filter((f) => !hasTag(f, RM_TAGS.Home) && !hasTag(f, RM_TAGS.Work)), [favourites]);

  // Every pick, clear, edit, or stop removal bumps the epoch; an async Google resolution only
  // applies if nothing happened since its click. Without this a slow resolve overwrites whatever
  // the user did in the meantime (re-pick, clear, splice-shifted stop indices).
  const pickEpochRef = useRef(0);

  const handleAddStop = () => {
    if (stops.length >= maxStops) return;
    dispatch(addStop());
    setStopTexts((prev) => [...prev, ""]);
    dispatch(setActiveField({ kind: "stop", index: stops.length }));
  };
  const handleRemoveStop = (index: number) => {
    pickEpochRef.current += 1;
    dispatch(removeStop(index));
    setStopTexts((prev) => prev.filter((_, i) => i !== index));
  };

  // Row picks target the field that was active when the row rendered (closure capture): async
  // Google resolution can land after focus moved on, and a stale write must hit the field the
  // search was for — setFieldLocation only advances focus if that field is still the active one.
  const writeToActiveField = (poi: PointsOfInterest) => {
    if (!activeField) return;
    pickEpochRef.current += 1;
    dispatch(setFieldLocation({ field: activeField, poi }));
  };
  const pickRemotePlace = (place: Parameters<typeof resolvePlace>[0]) => {
    const epoch = pickEpochRef.current;
    resolvePlace(place).then((poi) => {
      if (poi && pickEpochRef.current === epoch) writeToActiveField(poi);
    });
  };
  const handlePinOnMap = () => {
    if (!activeField) dispatch(setActiveField({ kind: "pickup" }));
    dispatch(setIsPinning(true));
  };

  // A field input gaining focus activates it — EXCEPT the focus headlessui restores to the just-pinned
  // (already-filled) field when the Pin-on-Map modal closes. Ignoring that one refocus lets the pin
  // flow finish like a normal pick: the reducer already advanced activeField to the next empty field
  // (or null), and the walk-through focus targets an EMPTY field, so it's never swallowed here.
  const fieldHasValue = (field: ActiveBookingField) =>
    field.kind === "pickup" ? !!pickup : field.kind === "destination" ? !!destination : !!stops[field.index];
  const focusField = (field: ActiveBookingField) => {
    if (ignorePinnedRefocusRef.current && fieldHasValue(field)) {
      ignorePinnedRefocusRef.current = false;
      // The refocus not only re-activates the field (blocked above) but also moves DOM focus (the ring +
      // caret) onto the just-pinned field. Redirect focus to where activeField actually is — the next
      // empty field gets focused with its ring, or focus is released when the route is complete — so the
      // pinned field doesn't keep the caret/ring. Deferred a tick so it runs after this onFocus settles.
      window.setTimeout(() => {
        if (activeField) inputRefs.current[fieldKey(activeField)]?.focus();
        else (document.activeElement as HTMLElement | null)?.blur();
      }, 0);
      return;
    }
    dispatch(setActiveField(field));
  };

  // Typing away from a committed selection reverts the field to "typing" (clears the POI), so the
  // clear ✕ returns; selecting again re-commits and shows the favourite star.
  const onPickupChange = (text: string) => {
    pickEpochRef.current += 1;
    setPickupText(text);
    setSearchText(text);
    if (pickup && text !== pickup.address) dispatch(setPickup(null));
  };
  const onDestinationChange = (text: string) => {
    pickEpochRef.current += 1;
    setDestinationText(text);
    setSearchText(text);
    if (destination && text !== destination.address) dispatch(setDestination(null));
  };
  const onStopChange = (index: number, text: string) => {
    pickEpochRef.current += 1;
    setStopTexts((prev) => prev.map((t, i) => (i === index ? text : t)));
    setSearchText(text);
    if (stops[index] && text !== stops[index]?.address) dispatch(setStop({ index, poi: null }));
  };

  // Every ✕ is clickable while ANOTHER field is focused (mousedown preventDefault keeps focus), so
  // only the active field's clear may reset the shared search query — otherwise clearing an idle
  // field would collapse the active field's live results.
  const clearFieldText = (field: ActiveBookingField) => {
    pickEpochRef.current += 1;
    if (isSameField(activeField, field)) setSearchText("");
    if (field.kind === "pickup") {
      setPickupText("");
      dispatch(setPickup(null));
    } else if (field.kind === "destination") {
      setDestinationText("");
      dispatch(setDestination(null));
    } else {
      setStopTexts((prev) => prev.map((t, i) => (i === field.index ? "" : t)));
      dispatch(setStop({ index: field.index, poi: null }));
    }
  };

  // Guests have no account to save a favourite against — hide the star entirely rather than
  // let it open a modal with nowhere to persist to.
  const trailingFor = (poi: PointsOfInterest | null, text: string, onClear: () => void) =>
    poi ? (
      !isGuest && <FavouriteButton saved={isFavourite(poi)} onClick={() => setSavePoi(poi)} />
    ) : text.trim() ? (
      <ClearButton onClear={onClear} />
    ) : undefined;

  // Everything the takeover's single input needs to act as a lens on the active field — one
  // switch, so per-field text/poi/placeholder/icon/write can't drift apart.
  const fieldView = (field: ActiveBookingField) => {
    if (field.kind === "pickup")
      return {
        text: pickupText,
        poi: pickup,
        placeholder: "Enter Pickup Location",
        renderIcon: () => <Icon name="dot" isCustom size="md" className="text-neutral-dark-gray" />,
        onChange: onPickupChange,
      };
    if (field.kind === "destination")
      return {
        text: destinationText,
        poi: destination,
        placeholder: "Dropoff",
        renderIcon: () => <Icon name="Location" size="md" variant="Bold" className="text-neutral-dark-gray" />,
        onChange: onDestinationChange,
      };
    return {
      text: stopTexts[field.index] ?? "",
      poi: stops[field.index] ?? null,
      placeholder: `Enter Extra Stop #${field.index + 1}`,
      renderIcon: () => <Icon name="Location" size="md" className="text-neutral-dark-gray" />,
      onChange: (text: string) => onStopChange(field.index, text),
    };
  };

  const suggestedShown = showAllSuggested ? providerPois : providerPois.slice(0, 2);
  const recentShown = showAllRecent ? recent : recent.slice(0, 2);

  // One results body serves the inline surface (md+) and the full-screen takeover (<md); null
  // while nothing is active so idle re-renders don't build a row tree nothing mounts.
  const resultsContent = activeField && (
    <>
      {isSearching && (
        <>
          {localMatches.map((poi) => (
            <SuggestionRow key={poiKey(poi)} poi={poi} onPick={writeToActiveField} />
          ))}
          {remoteMatches.map((place) => {
            const icon = suggestionIcon(undefined, true);
            return (
              <button
                key={place.place_id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickRemotePlace(place)}
                className="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-neutral-surface-gray"
              >
                <Icon name={icon.name} size="sm" className={clsx("mt-0.5 shrink-0", icon.className)} />
                <span className="min-w-0">
                  <Typography variant="action" className="block truncate">
                    {place.structured_formatting?.main_text ?? place.description}
                  </Typography>
                  <Typography variant="small-regular" className="block truncate text-neutral-dark-gray">
                    {place.description}
                  </Typography>
                </span>
              </button>
            );
          })}
          {localMatches.length === 0 && remoteMatches.length === 0 && (
            <Typography variant="small-regular" className="py-3 text-center text-neutral-dark-gray">
              {remotePending ? "Searching…" : "No matching locations found"}
            </Typography>
          )}
        </>
      )}

      {!isSearching && currentLocationPoi && (
        <>
          <GroupLabel>Current location</GroupLabel>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => writeToActiveField(currentLocationPoi)}
            className="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-neutral-surface-gray"
          >
            <Icon name="Gps" size="sm" className="mt-0.5 shrink-0 text-neutral-dark-gray" />
            <span className="min-w-0">
              <Typography variant="action" className="block truncate">
                {poiTitle(currentLocationPoi)}
              </Typography>
              <Typography variant="small-regular" className="block truncate text-neutral-dark-gray">
                {currentLocationPoi.address}
              </Typography>
            </span>
          </button>
        </>
      )}

      {!isSearching && suggestedShown.length > 0 && (
        <>
          <GroupLabel>Suggested</GroupLabel>
          {suggestedShown.map((poi) => (
            <SuggestionRow key={poiKey(poi)} poi={poi} onPick={writeToActiveField} />
          ))}
          {providerPois.length > 2 && (
            <ToggleLink
              label={showAllSuggested ? "View less" : "View more suggested locations"}
              onClick={() => setShowAllSuggested((v) => !v)}
            />
          )}
        </>
      )}

      {!isSearching && recentShown.length > 0 && (
        <>
          <GroupLabel>Recent</GroupLabel>
          {recentShown.map((poi) => (
            <SuggestionRow key={poiKey(poi)} poi={poi} onPick={writeToActiveField} />
          ))}
          {recent.length > 2 && (
            <ToggleLink label={showAllRecent ? "View less" : "View more from recent history"} onClick={() => setShowAllRecent((v) => !v)} />
          )}
        </>
      )}

      {!isSearching && homeWork.length > 0 && (
        <>
          <GroupLabel>Home/Work</GroupLabel>
          {homeWork.map((poi) => (
            <SuggestionRow key={poiKey(poi)} poi={poi} onPick={writeToActiveField} />
          ))}
        </>
      )}

      {!isSearching && otherFavourites.length > 0 && (
        <>
          <GroupLabel>Favourites</GroupLabel>
          {otherFavourites.map((poi) => (
            <SuggestionRow key={poiKey(poi)} poi={poi} onPick={writeToActiveField} />
          ))}
        </>
      )}
    </>
  );

  return (
    <div
      ref={rootRef}
      // Fixed-layout card like the other step panels: bounded height, the Next Step footer stays
      // pinned, and each region below owns exactly one scroller — the inputs, then the results
      // surface. (Was one big scroll container, which let Next Step scroll off on a short viewport.)
      className={clsx("flex min-h-0 flex-col overflow-hidden rounded-2xl border border-neutral-mid-gray bg-white shadow-lg", className)}
    >
      <ViewOnlyFieldset>
        {/* Inputs — their own scroller, so a long stop list scrolls here and nowhere else. This was
            nested inside a body scroller that ALSO held the results surface, and the surface (flex
            basis 0) absorbed none of the shrink a tall stop list forced: at the 10-stop cap it
            measured 37px tall holding a 480px list, nested inside the outer scroller. min-h-0 lets
            this region shrink below its content (flex items default to min-height:auto) so the
            surface below keeps the floor it sets for itself.

            That floor is a length, not a percentage, because the card's height is not always
            definite: the surface mounts on `isMdUp` (a VIEWPORT media query) but the card gets its
            top+bottom anchors from an @container query on the sheet's width (see ClientBooking.tsx),
            and below that container threshold it is height:auto capped by max-height. Measured at
            the 10-stop cap with a 760px container: a 45% floor came out 222px (not 45% of the
            594px card — i.e. inoperative, just leftover space), while this length floor holds
            exactly. Not a collapse to zero, but not a floor either.

            The floor drops on a short viewport because it is a floor, not a share: `flex-1` gives
            the surface basis 0, so the inputs region absorbs every bit of a height deficit. At
            1200x400 the card is 192px, and a flat 16rem floor left the inputs 40px and pushed the
            surface 105px past the card's `overflow-hidden` edge — its last rows unreachable, which
            is worse than a short list. 8rem below 700px keeps both regions on-screen. */}
        <div className="min-h-0 overflow-y-auto p-5">
          <Typography variant="h2">Address</Typography>
          <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
            Provide the pickup and drop-off locations to ensure a smooth and hassle-free ride
          </Typography>

          <div className="mt-4 flex flex-col gap-3">
            <BookingLocationInput
              ref={registerInput("pickup")}
              value={pickupText}
              onChange={onPickupChange}
              onFocus={() => focusField({ kind: "pickup" })}
              onBlur={handleBlurAway}
              placeholder="Enter Pickup Location"
              infoMessage={ADDRESS_INFO}
              warningMessage={isUnvalidatedLocation(pickup, jobVerified) ? UNVALIDATED_ADDRESS : undefined}
              active={activeField?.kind === "pickup"}
              trailing={trailingFor(pickup, pickupText, () => clearFieldText({ kind: "pickup" }))}
              renderIcon={() => <Icon name="dot" isCustom size="md" className="text-neutral-dark-gray" />}
            />

            {/* Flight number (tap to edit/add) + the terminal parsed from the selected airport
              address (omitted when the POI has none). */}
            {pickup && isAirportPickupSelected && (
              <div className="-mt-1 ml-3 flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setFlightModalOpen(true)}
                  className="flex items-center gap-2 self-start text-neutral-dark-gray hover:text-primary"
                >
                  <Icon name="Airplane" size="sm" variant="Linear" className="shrink-0" />
                  <Typography variant="muted">{pickupFlightNumber ?? "Add flight details"}</Typography>
                  {/* Only once there IS a flight, and only while the booking can still be changed. The
                    row has always opened the modal and the pencil makes that visible — so on a
                    view-only booking it would promise an edit that isn't on offer. Beside "Add flight
                    details" it would be wrong too: that row adds rather than edits, and its own
                    wording is already the affordance. */}
                  {pickupFlightNumber && !viewOnly && <Icon name="Edit2" size="sm" className="shrink-0 text-info" />}
                </button>
                {pickupTerminal && (
                  <div className="flex items-center gap-2 text-neutral-dark-gray">
                    <Icon name="dot" isCustom size="sm" className="shrink-0" />
                    <Typography variant="muted">{pickupTerminal}</Typography>
                  </div>
                )}
              </div>
            )}

            {stops.map((stop, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <BookingLocationInput
                    ref={registerInput(`stop-${index}`)}
                    value={stopTexts[index] ?? ""}
                    onChange={(text) => onStopChange(index, text)}
                    onFocus={() => focusField({ kind: "stop", index })}
                    onBlur={handleBlurAway}
                    placeholder={`Enter Extra Stop #${index + 1}`}
                    infoMessage={ADDRESS_INFO}
                    warningMessage={isUnvalidatedLocation(stop, jobVerified) ? UNVALIDATED_ADDRESS : undefined}
                    active={activeField?.kind === "stop" && activeField.index === index}
                    trailing={trailingFor(stop, stopTexts[index] ?? "", () => clearFieldText({ kind: "stop", index }))}
                    renderIcon={() => <Icon name="Location" size="md" className="text-neutral-dark-gray" />}
                  />
                </div>
                <button
                  type="button"
                  aria-label={`Remove stop ${index + 1}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleRemoveStop(index)}
                  className="mt-3 shrink-0 text-neutral-dark-gray hover:text-danger"
                >
                  <Icon name="Minus" size="md" />
                </button>
              </div>
            ))}

            <BookingLocationInput
              ref={registerInput("destination")}
              value={destinationText}
              onChange={onDestinationChange}
              onFocus={() => focusField({ kind: "destination" })}
              onBlur={handleBlurAway}
              placeholder="Dropoff"
              infoMessage={ADDRESS_INFO}
              warningMessage={isUnvalidatedLocation(destination, jobVerified) ? UNVALIDATED_ADDRESS : undefined}
              active={activeField?.kind === "destination"}
              trailing={trailingFor(destination, destinationText, () => clearFieldText({ kind: "destination" }))}
              renderIcon={() => <Icon name="Location" size="md" variant="Bold" className="text-neutral-dark-gray" />}
            />

            {stops.length < maxStops && (
              <div>
                <Button
                  variant="tertiary"
                  size="sm"
                  startIcon="Add"
                  // Keep the current input focused through the click — the new stop takes over focus.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleAddStop}
                >
                  Add Stop
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* The shared results surface — md+ only (mobile uses the full-screen takeover below).
          An edited 3+ char query shows live search results for the active field; otherwise the
          grouped default suggestions. */}
        {isMdUp && activeField && !viewOnly && (
          <div
            // Clicks anywhere in the surface (rows, whitespace) must not blur the active input —
            // except scrollbar-track hits, where preventDefault would cancel thumb dragging in
            // Chromium. onBlur: Tab past the last row still closes the surface.
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && e.nativeEvent.offsetX >= e.currentTarget.clientWidth) return;
              e.preventDefault();
            }}
            onBlur={handleBlurAway}
            className="flex min-h-[8rem] flex-1 flex-col overflow-y-auto border-t border-neutral-mid-gray px-5 pb-5 pt-4 [@media(min-height:700px)]:min-h-[16rem]"
          >
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handlePinOnMap} className="mx-auto mb-2 block">
              <Typography variant="action" className="text-info">
                📍 Pin Location on Map
              </Typography>
            </button>

            {resultsContent}
          </div>
        )}
      </ViewOnlyFieldset>

      {/* Next Step — on mobile the card always carries it (the takeover covers the card while
          browsing); on md+ it shows only while no field is active (the surface replaces it).
          Pinned footer (shrink-0) matching TripDetailsPanel: right-aligned md button (no Previous on
          the first step), full-width divider on mobile only. */}
      {(!activeField || !isMdUp) && (
        <div className="mt-auto flex shrink-0 items-center justify-end gap-3 border-t border-neutral-mid-gray p-4 md:border-t-0">
          <Button variant="primary" size="md" disabled={!pickup || !destination} onClick={onNext}>
            Next Step
          </Button>
        </div>
      )}

      {/* Mobile full-screen search takeover: back arrow, one input bound to the active field, the
          shared results, and Pin-on-Map in a sticky bottom bar. Picks walk through the remaining
          empty fields; the last fill clears activeField and closes it. */}
      {!isMdUp &&
        activeField &&
        (() => {
          const view = fieldView(activeField);
          return createPortal(
            <div className="fixed inset-0 z-50 flex flex-col bg-white">
              <div className="flex flex-col gap-3 p-5 pb-4">
                <IconButton
                  variant="custom"
                  iconName="ArrowLeft"
                  iconSize="md"
                  aria-label="Back"
                  onClick={() => dispatch(setActiveField(null))}
                  className="self-start text-neutral-black"
                />
                <Typography variant="h2" className="text-neutral-dark-gray">
                  Where to?
                </Typography>
                <BookingLocationInput
                  ref={takeoverInputRef}
                  value={view.text}
                  onChange={view.onChange}
                  placeholder={view.placeholder}
                  infoMessage={ADDRESS_INFO}
                  active
                  trailing={trailingFor(view.poi, view.text, () => clearFieldText(activeField))}
                  renderIcon={view.renderIcon}
                />
              </div>
              <div
                className="min-h-0 flex-1 overflow-y-auto border-t border-neutral-mid-gray px-5 pb-5 pt-2"
                // preventDefault keeps the input focused through row taps so the keyboard doesn't
                // flicker closed; the takeover itself only closes via Back or the final pick.
                onMouseDown={(e) => e.preventDefault()}
              >
                {resultsContent}
              </div>
              <div className="border-t border-neutral-mid-gray bg-white p-3">
                <button type="button" onClick={handlePinOnMap} className="mx-auto block py-1.5">
                  <Typography variant="action" className="text-info">
                    📍 Pin Location on Map
                  </Typography>
                </button>
              </div>
            </div>,
            document.body
          );
        })()}

      <PinLocationModal allowedCountries={searchCountries} />
      <FlightDetailsModal
        open={flightModalOpen}
        initialValue={pickupFlightNumber ?? ""}
        onConfirm={(flightNumber) => {
          confirmFlightNumber(flightNumber);
          setFlightModalOpen(false);
        }}
        onNotFlying={() => {
          notFlying();
          setFlightModalOpen(false);
        }}
        onDismiss={() => setFlightModalOpen(false)}
      />
      <AddEditLocationModal
        open={!!savePoi}
        mode="add"
        location={savePoi ?? undefined}
        accountId={accountId}
        hideCategory
        title="Save to Favourites"
        description="Add to your favourite locations for faster and easier bookings"
        submitLabel="Save to Favourites"
        onClose={() => setSavePoi(null)}
      />
    </div>
  );
};
